/**
 * Axios instance configured for the Rails backend API.
 *
 * Request interceptor: Injects a Bearer token from localStorage. Handles
 * JSON-stringified values because Jotai's atomWithStorage wraps stored
 * strings in quotes (e.g., `"eyJhbG..."` instead of `eyJhbG...`).
 *
 * Response interceptor: On 401 (or JWT decode errors on any status),
 * clears auth state and redirects. 403s are business-rule rejections —
 * the calling page handles them. Provisional users are redirected to the
 * quick-start flow (/start/:storyId) so they can re-enter their email
 * for a fresh JWT; full users go to `/`.
 * @module apiClient
 */

import axios from 'axios'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_MASTERMIND_API,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
})

// Auth endpoints that should NOT send a Bearer token.
// PUT /signup is the provisional-user upgrade endpoint — it NEEDS a token
// so the backend knows which account to upgrade. Only POST /signup (new
// registration) should skip auth.
const AUTH_ENDPOINTS = ['/login', '/logout', '/password', '/provisional_auth', '/claim']
const UNAUTHENTICATED_SIGNUP_METHODS = ['post']

function isUnauthenticatedRequest(config) {
  if (AUTH_ENDPOINTS.some((p) => config.url?.startsWith(p))) return true
  if (config.url?.startsWith('/signup') && UNAUTHENTICATED_SIGNUP_METHODS.includes(config.method)) return true
  return false
}

// Request interceptor to inject Bearer token (skip auth endpoints)
apiClient.interceptors.request.use(
  (config) => {
    if (isUnauthenticatedRequest(config)) return config

    let token = localStorage.getItem('bw_authToken')
    if (token) {
      // Jotai's atomWithStorage JSON-stringifies values, so parse if needed
      try {
        token = JSON.parse(token)
      } catch (e) {
        // Token is already a plain string
      }
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// JWT-related error messages that indicate a corrupt/expired token
const JWT_ERRORS = [
  'not enough or too many segments',
  'signature verification',
  'expired token',
  'decode error',
  'invalid token',
  'nil json web token',
]

function isJwtError(error) {
  const message = (
    error.response?.data?.error ||
    error.response?.data?.message ||
    ''
  ).toLowerCase()
  return JWT_ERRORS.some((e) => message.includes(e))
}

function clearAuthAndRedirect() {
  const userStr = localStorage.getItem('bw_authUser')
  let isProvisional = false
  try { isProvisional = JSON.parse(userStr)?.provisional === true } catch (e) {}

  localStorage.removeItem('bw_authToken')
  localStorage.removeItem('bw_authUser')
  localStorage.removeItem('bw_isProvisional')

  // Already on an unauthenticated landing page? Clearing the token is enough —
  // a hard redirect here would reload the same page, and any authed request it
  // fires re-triggers this, producing an infinite refresh loop. Bail out.
  const path = window.location.pathname
  if (path === '/login' || path.startsWith('/start/')) {
    return
  }

  // Preserve current location so the user can return after re-authenticating
  const currentPath = path + window.location.search
  if (currentPath !== '/' && currentPath !== '/login') {
    sessionStorage.setItem('bw_returnTo', currentPath)
  }

  if (isProvisional) {
    const match = path.match(/\/play\/(.+)/)
    const storyId = match?.[1]
    window.location.href = storyId ? `/start/${storyId}` : '/login'
  } else {
    window.location.href = '/login'
  }
}

// Response interceptor to handle auth failures and corrupt tokens
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = isUnauthenticatedRequest(error.config || {})
    const status = error.response?.status

    // JWT decode errors can happen on any endpoint if the stored token is corrupt.
    // Clear the bad token so the user can recover by logging in again.
    if (isJwtError(error)) {
      localStorage.removeItem('bw_authToken')
      localStorage.removeItem('bw_authUser')
      localStorage.removeItem('bw_isProvisional')
      // Don't redirect for auth endpoints — let the login page handle the error normally
      if (!isAuthEndpoint) {
        clearAuthAndRedirect()
      }
      return Promise.reject(error)
    }

    // Expired/missing tokens come back as 401. A 403 means "you are
    // authenticated but not authorized for this specific action" — those
    // are business-rule rejections (e.g. "you don't have Group access to
    // that story") that the calling page should surface as an inline
    // error, not a session wipe.
    if (!isAuthEndpoint && status === 401) {
      clearAuthAndRedirect()
    }

    return Promise.reject(error)
  }
)

export default apiClient
